import { Navigate, useLocation } from 'react-router';
import { ReactNode } from 'react';
import {
  getCurrentUuid,
  isAuthenticated, isProfileComplete,
  setCurrentLocationPath,
} from '@/utils/helpers/authHelpers';
import { useGetCurrentUser } from '@/hooks/useGetCurrentUser';
import SuspenseSpinner from '@/content/components/common/SuspenseLoaderWrapper/SuspenseSpinner';
import { globalLogOut } from '@/lib/auth/authServices';
import FRONTEND_ROUTES from '@/consts/frontendRoutes';
import { COMMON_ERRORS } from '@/consts/common';
import {
  PERMISSION_ACTION,
  UI_VIEW,
  canAccessView,
  canPerformAction,
} from '@/lib/authZ';
import { isUsageTimeLimitError } from '@/lib/auth/usageTimeLimitLockout';

interface AuthenticatedRouteProps {
  children: ReactNode
}

const {
  HOME, COMPLETE_PROFILE, DASHBOARD,
  SELF_USER_MANAGEMENT,
  INDEPENDENT_USER_MANAGEMENT,
  ORGANIZATION_MANAGEMENT,
  LICENSE_MANAGEMENT,
} = FRONTEND_ROUTES;

const ORGANIZATION_DETAIL_PATH_PATTERN = /^\/organization-management\/[^/]+/;

const AuthenticatedRoute = ({
  children,
}: AuthenticatedRouteProps) => {
  const location = useLocation();
  const currentUserUUid = getCurrentUuid();
  const { isPending, error: errorGetUserById } = useGetCurrentUser();

  try {
    if (!isAuthenticated()) {
      setCurrentLocationPath(location.pathname);
      return <Navigate to={HOME} replace />;
    }

    if (isAuthenticated() && !currentUserUUid) {
      globalLogOut();
      return <Navigate to={HOME} replace />;
    }

    if (isPending) {
      return <SuspenseSpinner />;
    }

    if (
      errorGetUserById
      && !isUsageTimeLimitError(errorGetUserById)
      && errorGetUserById.message === COMMON_ERRORS.UNEXPECTED_ERROR
    ) {
      setCurrentLocationPath(location.pathname);
      globalLogOut();
      return <Navigate to={HOME} replace />;
    }

    if (!isProfileComplete() && location.pathname !== COMPLETE_PROFILE) {
      return <Navigate to={COMPLETE_PROFILE} replace />;
    }

    if (isProfileComplete() && location.pathname === COMPLETE_PROFILE) {
      return <Navigate to={DASHBOARD} replace />;
    }

    if (!canAccessView(UI_VIEW.organizationUsersTable)
      && location.pathname === SELF_USER_MANAGEMENT) {
      return <Navigate to={DASHBOARD} replace />;
    }

    if (!canAccessView(UI_VIEW.independentUsersTable)
      && location.pathname === INDEPENDENT_USER_MANAGEMENT) {
      return <Navigate to={DASHBOARD} replace />;
    }

    if (!canAccessView(UI_VIEW.organizationsTable)
      && location.pathname === ORGANIZATION_MANAGEMENT) {
      return <Navigate to={DASHBOARD} replace />;
    }

    if (!canPerformAction(PERMISSION_ACTION.viewOrganizationLicensePool)
      && location.pathname === LICENSE_MANAGEMENT) {
      return <Navigate to={DASHBOARD} replace />;
    }

    if (!canAccessView(UI_VIEW.organizationProfilePage)
      && ORGANIZATION_DETAIL_PATH_PATTERN.test(location.pathname)) {
      return <Navigate to={DASHBOARD} replace />;
    }

    return children;
  } catch (e) {
    return <Navigate to={HOME} replace />;
  }
};

export default AuthenticatedRoute;
